import { NgModule, Optional, SkipSelf, inject, provideAppInitializer } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HeaderComponent } from './components/header/header.component';

import { MissingTranslationHandler, TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { HTTP_INTERCEPTORS, HttpClient } from '@angular/common/http';
import { environment } from '@environment/environment.development';
import {
    HttpLoaderFactory,
    MissingTranslationService,
    LocalizationService,
} from './services/infrastructure/localization/localization.service';
import { RouterModule } from '@angular/router';
import { NotificationService } from './services/infrastructure/notification/notification.service';
import { AuthInterceptor } from './interceptors/auth.interceptor';
import { AuthGuard } from './guards/auth.guard';
import { ErrorInterceptor } from './interceptors/error.interceptor';
import { CreditsInterceptor } from './interceptors/credits.interceptor';
import { IconsModule } from '@app/shared/icons.module';
import { AuthGuestGuard } from './guards/auth-guest.guard';
import { RolesGuard } from './guards/roles.guard';
import { ForbiddenComponent } from './components/forbidden/forbidden.component';
import { firstValueFrom } from 'rxjs';
import { AuthApiService } from '@app/modules/auth/services/auth-api/auth-api.service';
import { TempPageGuard } from './guards/temp-page.guard';

/**
 * Collection of all core services that should be singletons
 * Provides type safety and easier maintenance
 */
const CORE_SERVICES = [
    // Infrastructure Services
    NotificationService,
    LocalizationService,
    AuthApiService,
    LocalizationService,
];

/**
 * HTTP Interceptors configuration
 * Order matters - ErrorInterceptor should be last to catch all errors
 */
const CORE_INTERCEPTORS = [
    {
        provide: HTTP_INTERCEPTORS,
        useClass: AuthInterceptor,
        multi: true,
    },
    {
        provide: HTTP_INTERCEPTORS,
        useClass: CreditsInterceptor,
        multi: true,
    },
    {
        provide: HTTP_INTERCEPTORS,
        useClass: ErrorInterceptor,
        multi: true,
    },
];

/**
 * Route Guards configuration
 */
const CORE_GUARDS = [AuthGuard, AuthGuestGuard, RolesGuard, TempPageGuard];

/**
 * Core module containing singleton services, global components, and infrastructure
 * Should be imported only once in AppModule to ensure singleton behavior
 *
 * Features:
 * - Centralized error handling
 * - HTTP interceptors for loading states and error handling
 * - Translation support with missing translation handling
 * - Global header component
 * - Singleton services for state management
 */
@NgModule({
    declarations: [HeaderComponent, ForbiddenComponent],
    imports: [
        IconsModule,
        CommonModule,
        RouterModule,

        // Translation module configuration
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient],
            },
            missingTranslationHandler: {
                provide: MissingTranslationHandler,
                useClass: MissingTranslationService,
            },
            useDefaultLang: true,
            defaultLanguage: environment.localization.defaultLanguage,
            isolate: false, // Allow sharing translations across lazy modules
        }),
    ],
    exports: [HeaderComponent, TranslateModule],
    providers: [
        ...CORE_SERVICES,
        ...CORE_INTERCEPTORS,
        ...CORE_GUARDS,
        provideAppInitializer(() => {
            const initializerFn = ((auth: AuthApiService, localization: LocalizationService) => {
                return (): Promise<void> => {
                    localization.init();
                    auth.init();

                    return firstValueFrom(auth.inited$);
                };
            })(inject(AuthApiService), inject(LocalizationService));
            return initializerFn();
        }),
    ],
})
export class CoreModule {
    /**
     * Prevents multiple imports of CoreModule
     * Ensures singleton behavior of core services
     */
    constructor(@Optional() @SkipSelf() parentModule: CoreModule) {
        if (parentModule) {
            throw new Error('CoreModule is already loaded. Import it in the AppModule only.');
        }
    }
}
